// What ⌘K finds: pages and their views, things to do, and (from public.dashboard_search) clients, websites and leads
// by name, phone or record number. Every result opens a page, sometimes with what to show on it.
import {allViews,canOpen,menuPages,visibleViews,type Viewer} from './nav-model';
import {leadParams,when} from './today-model';

export type Group='Pages'|'Actions'|'Clients'|'Websites'|'Leads';
export type Hit={key:string;group:Group;label:string;detail?:string;to:string;params?:Record<string,string>};
export type SearchResult={
 query:string;
 clients:{business_id:string;name:string;manager:string|null;active:boolean}[];
 websites:{website_id:number;website:string;client:string|null}[];
 leads:{lead_id:number;record_number:number;name:string|null;phone_last4:string|null;kind:'call'|'form';at:string;website:string|null;client:string|null;duplicate:boolean}[];
};

/** Things people come to the hub to do, and the page that does each. Offered only when that page is open to them. */
export const ACTIONS:{label:string;to:string;keywords:string}[]=[
 {label:'Pause all sending',to:'lead-delivery',keywords:'stop emergency halt texts emails'},
 {label:'Link a Google or Meta campaign to a client',to:'paid',keywords:'ads spend campaign account match'},
 {label:'Set this month’s billing goal',to:'kpi',keywords:'target revenue goal'},
 {label:'Fill in client records from a spreadsheet',to:'records',keywords:'paste billing fee price goal stage trial closer bulk'},
 {label:'Invite someone or make a setup link',to:'team',keywords:'user login access add person password'},
 {label:'Approve an email flow',to:'messages',keywords:'resend weekly report confirmation template'},
 {label:'Give a website to a client',to:'routing',keywords:'route assign recipients split'},
 {label:'Change how a website’s calls are answered',to:'phone-routing',keywords:'ai answering script forward phone'},
 {label:'Sell a website with no client',to:'sales',keywords:'unsold territory buyer'},
 {label:'Assign an account manager',to:'account-managers',keywords:'manager client assignment'},
 {label:'See what is failing',to:'health',keywords:'errors alerts sync broken data health'},
 {label:'See today’s work',to:'today',keywords:'home missed calls reached no one quiet'},
];

const words=(q:string)=>q.toLowerCase().split(/\s+/).filter(Boolean);
/** 0: no match. Higher when the first word starts the text or one of its words. */
export function score(text:string,query:string):number{
 const ws=words(query),t=text.toLowerCase();
 if(!ws.length||!ws.every(w=>t.includes(w)))return 0;
 return t.startsWith(ws[0])?3:t.includes(` ${ws[0]}`)?2:1;
}
const ranked=<T,>(items:T[],rank:(x:T)=>number,limit:number)=>items.map((x,i)=>({x,i,s:rank(x)})).filter(r=>r.s>0).sort((a,b)=>b.s-a.s||a.i-b.i).slice(0,limit).map(r=>r.x);
/** A match in the name ranks above a match only in the description. */
const best=(name:string,all:string,query:string)=>{const n=score(name,query);return n?n+3:score(all,query);};

/** Pages and views matching the query; with no query, every page in the menu. */
export function pageHits(query:string,who:Viewer):Hit[]{
 if(!query.trim())return menuPages(who).map(p=>({key:`page:${p.id}`,group:'Pages',label:p.label,detail:visibleViews(p,who).length>1?visibleViews(p,who).map(v=>v.label).join(' · '):undefined,to:p.id}));
 return ranked(allViews(who),({page,view})=>best(`${page.label} ${view.label}`,`${page.label} ${view.label} ${view.keywords??''} ${view.summary}`,query),6)
  .map(({page,view})=>({key:`view:${view.id}`,group:'Pages',label:view.label===page.label?page.label:`${page.label} › ${view.label}`,detail:view.summary,to:view.id}));
}
export function actionHits(query:string,who:Viewer):Hit[]{
 if(!query.trim())return [];
 return ranked(ACTIONS.filter(a=>canOpen(a.to,who)),a=>best(a.label,`${a.label} ${a.keywords}`,query),4)
  .map(a=>({key:`action:${a.label}`,group:'Actions',label:a.label,to:a.to}));
}
export function resultHits(r:SearchResult,who:Viewer):Hit[]{
 const hits:Hit[]=[];
 if(canOpen('clients',who))for(const c of r.clients)hits.push({key:`client:${c.business_id}`,group:'Clients',label:c.name,
  detail:[c.manager?`Managed by ${c.manager}`:'No account manager',c.active?null:'not live'].filter(Boolean).join(' · '),to:'clients',params:{client:c.business_id}});
 if(canOpen('websites',who))for(const w of r.websites)hits.push({key:`site:${w.website_id}`,group:'Websites',label:w.website,detail:w.client??'No client',to:'websites',params:{site:w.website}});
 if(canOpen('leads',who))for(const l of r.leads)hits.push({key:`lead:${l.lead_id}`,group:'Leads',label:`#${l.record_number} · ${l.name??'No name given'}`,
  detail:[l.kind==='form'?'Form':'Call',l.phone_last4?`phone ending ${l.phone_last4}`:null,when(l.at),l.client??l.website,l.duplicate?'duplicate':null].filter(Boolean).join(' · '),
  to:'leads',params:leadParams(l.record_number)});
 return hits;
}
/** Queries Supabase is asked about: two characters or more, trimmed as it trims them. */
export const serverQuery=(q:string)=>q.trim().slice(0,80);
export const searchable=(q:string)=>serverQuery(q).length>=2;
